import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  Button,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { io } from "socket.io-client";

import EmailPanelIzquierdo from "./EmailPanelIzquierdo";
import EmailPanelDerecho from "./EmailPanelDerecho";

import EmailEnviarPro from "../eNuevo/EmailEnviarPro";
import EmailContestar from "../eContestar/EmailContestar";
import EmailReenviar from "../eReenviar/EmailReenviar";

import AccountManager from "../../cuentas/AccountManager";

import DeleteIcon from "@mui/icons-material/Delete";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";

const API = process.env.REACT_APP_API_URL;

export default function EmailGeneral() {
  const [threads, setThreads] = useState({});
  const [selectedPerson, setSelectedPerson] = useState(null);

  const [openAccounts, setOpenAccounts] = useState(false);

  const [showSendPanel, setShowSendPanel] = useState(false);

  const [showReplyPanel, setShowReplyPanel] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  const [showForwardPanel, setShowForwardPanel] = useState(false);
  const [forwardEmail, setForwardEmail] = useState(null);
  const [forwardHtml, setForwardHtml] = useState("");
  const [forwardOriginalAttachments, setForwardOriginalAttachments] = useState(
    []
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [replyHtml, setReplyHtml] = useState("");

  const [accounts, setAccounts] = useState([]);

  const [modoVista, setModoVista] = useState("email"); // "email" | "whatsapp"

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const [attachmentsByEmail, setAttachmentsByEmail] = useState({});
  const [bodyCache, setBodyCache] = useState({});

  const [selectedEmails, setSelectedEmails] = useState([]); // selección múltiple
  const [selectionMode, setSelectionMode] = useState(false); // si hay o no selección

  const [selectionBarMode, setSelectionBarMode] = useState("top");

  const [showDeleteSelectedDialog, setShowDeleteSelectedDialog] =
    useState(false);

  /* ---------------- LOAD ACCOUNTS ---------------- */
  useEffect(() => {
    axios.get(`${API}/auth/accounts`).then((res) => setAccounts(res.data));
  }, []);

  /* ---------------- LOAD EMAILS ---------------- */
  const loadEmails = async () => {
    const res = await axios.get(`${API}/emails-from-db`);
    const data = res.data;

    const summary = await axios.get(`${API}/emails/attachments-summary`);
    const hasAttachmentsMap = summary.data;

    // Marca emails con adjuntos
    for (const person in data) {
      data[person] = data[person].map((email) => ({
        ...email,
        hasAttachments: (hasAttachmentsMap[email.gmailId] || 0) > 0,
      }));
    }

    setThreads(data);
  };

  const buildConversationThreads = (rawThreads) => {
    const result = {};

    Object.values(rawThreads).forEach((emails) => {
      emails.forEach((email) => {
        const account = email.accountEmail;
        if (!account) return;

        const contact = email.from === account ? email.to : email.from;

        if (!contact) return;

        const key = `${account}::${contact}`;

        if (!result[key]) result[key] = [];
        result[key].push(email);
      });
    });

    return result;
  };

  const conversationThreads = React.useMemo(
    () => buildConversationThreads(threads),
    [threads]
  );

  useEffect(() => {
    loadEmails();
  }, []);

  /* ---------------- SOCKET SYNC ---------------- */
  useEffect(() => {
    const socket = io(API);
    socket.on("emails-updated", loadEmails);
    return () => socket.disconnect();
  }, []);

  /* ---------------- LOAD BODY ---------------- */
  const loadBodyCached = async (gmailId) => {
    if (bodyCache[gmailId]) return bodyCache[gmailId];

    const res = await axios.get(`${API}/emails/${gmailId}/body`);
    const html = res.data.html || res.data.text || "";

    setBodyCache((prev) => ({ ...prev, [gmailId]: html }));
    return html;
  };

  /* ---------------- LOAD ATTACHMENTS ---------------- */
  const loadAttachments = async (gmailId) => {
    const res = await axios.get(`${API}/emails/${gmailId}/attachments`);
    const files = res.data;

    setAttachmentsByEmail((prev) => ({ ...prev, [gmailId]: files }));
  };

  /* ---------------- TOGGLE READ ---------------- */
  const toggleRead = async (gmailId) => {
    const res = await axios.post(`${API}/emails/${gmailId}/toggle-read`);
    const newState = res.data.isRead;

    setThreads((prev) => {
      const updated = { ...prev };
      for (const p in updated) {
        updated[p] = updated[p].map((e) =>
          e.gmailId === gmailId ? { ...e, isRead: newState } : e
        );
      }
      return updated;
    });
  };

  /* ---------------- DELETE ---------------- */
  const borrar = async () => {
    await axios.delete(`${API}/emails/${deleteId}/permanent`);

    setThreads((prev) => {
      const updated = { ...prev };
      for (const p in updated) {
        updated[p] = updated[p].filter((e) => e.gmailId !== deleteId);
      }
      return updated;
    });

    setShowDeleteDialog(false);
  };

  /* ---------------- OPEN FORWARD ---------------- */
  const openForward = async (email) => {
    setForwardEmail(email);

    const res = await axios.get(
      `${API}/emails/${email.gmailId}/attachments/full`
    );
    const originalFiles = res.data;

    const body = await loadBodyCached(email.gmailId);

    const forwardedBlock = `
      <br/>
      ---------- Mensaje reenviado ----------<br/>
      <b>De:</b> ${email.from}<br/>
      <b>Fecha:</b> ${new Date(email.date).toLocaleString("es-ES")}<br/>
      <b>Asunto:</b> ${email.subject}<br/>
      <b>Para:</b> ${email.to}<br/><br/>
      ${body}
    `;

    setForwardHtml(forwardedBlock);
    setForwardOriginalAttachments(originalFiles);
    setShowForwardPanel(true);
  };

  function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00ffffff).toString(16).toUpperCase();
    return "#" + "000000".substring(0, 6 - c.length) + c;
  }

  function stringToPastelColor(seed) {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 80%)`;
  }

  function getAccountColor(email) {
    const account = accounts.find((a) => a.id === email.account_id);
    if (!account) return "#e2e8f0"; // gris claro

    return stringToPastelColor(account.email);
  }

  const toggleSelectEmail = (gmailId) => {
    setSelectedEmails((prev) => {
      if (prev.includes(gmailId)) {
        const updated = prev.filter((id) => id !== gmailId);
        if (updated.length === 0) setSelectionMode(false);
        return updated;
      } else {
        setSelectionMode(true);
        return [...prev, gmailId];
      }
    });
  };

  const clearSelection = () => {
    setSelectedEmails([]);
    setSelectionMode(false);
  };

  const deleteSelected = async () => {
    try {
      // Ejecuta todas las llamadas en paralelo
      await Promise.all(
        selectedEmails.map((id) =>
          axios.delete(`${API}/emails/${id}/permanent`).catch(() => null)
        )
      );

      clearSelection();
      loadEmails();
    } catch (err) {
      console.error("Error al eliminar emails:", err);
    }
  };

  const confirmDeleteSelected = async () => {
    try {
      await Promise.all(
        selectedEmails.map((id) =>
          axios.delete(`${API}/emails/${id}/permanent`).catch(() => null)
        )
      );

      clearSelection();
      loadEmails();
      setShowDeleteSelectedDialog(false);
    } catch (err) {
      console.error("Error al eliminar emails:", err);
    }
  };

  const markSelectedAsRead = async () => {
    for (const id of selectedEmails) {
      // 1) Pido el estado actual
      const email = Object.values(threads)
        .flat()
        .find((e) => e.gmailId === id);

      // 2) Si YA está leído → no hago nada
      if (email?.isRead) continue;

      // 3) Sino → toggle
      await axios.post(`${API}/emails/${id}/toggle-read`);
    }

    clearSelection();
    loadEmails();
  };

  const markSelectedAsUnread = async () => {
    for (const id of selectedEmails) {
      const email = Object.values(threads)
        .flat()
        .find((e) => e.gmailId === id);

      if (!email || !email.isRead) continue;

      await axios.post(`${API}/emails/${id}/toggle-read`);
    }

    clearSelection();
    loadEmails();
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* PANEL IZQUIERDO */}
      <EmailPanelIzquierdo
        threads={conversationThreads}
        selectedPerson={selectedPerson}
        setSelectedPerson={setSelectedPerson}
        search={search}
        setSearch={setSearch}
        filter={filter}
        setFilter={setFilter}
        modoVista={modoVista}
        setModoVista={setModoVista}
        setOpenAccounts={setOpenAccounts}
        setShowSendPanel={setShowSendPanel} // 🔥 AÑADIDO
        accounts={accounts} // 🔥 AÑADIDO
      />

      {selectionMode && (
        <>
          {/* 🔵 SWITCH DE MODOS — cambia posición según top / side */}
          <Box
            sx={{
              position: "fixed",
              zIndex: 100,
              display: "flex",

              // ⬅️ A/D horizontal si barra está arriba, vertical si está al lado
              flexDirection: selectionBarMode === "side" ? "column" : "row",
              gap: 1,

              bgcolor: "#ffffffee",
              backdropFilter: "blur(8px)",
              p: 1,
              borderRadius: "16px",
              boxShadow: "0 3px 10px rgba(0,0,0,0.15)",

              // ⬅️ REGLAS DE POSICIÓN SEGÚN MODO
              ...(selectionBarMode === "side"
                ? {
                    // 📌 LATERAL DERECHO — encima de los iconos
                    top: "43%",
                    right: 20,
                    transform: "translateY(-160%)",
                  }
                : {
                    // 📌 MODO ARRIBA — encima de la barra superior
                    top: 5,
                    left: 500,
                    transform: "none",
                  }),
            }}
          >
            {/* Botón A */}
            <IconButton
              size="small"
              onClick={() => setSelectionBarMode("top")}
              sx={{
                width: 30,
                height: 30,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: selectionBarMode === "top" ? "#3b82f6" : "white",
                color: selectionBarMode === "top" ? "white" : "#475569",
                border: "1px solid #cbd5e1",
                "&:hover": {
                  bgcolor: selectionBarMode === "top" ? "#2563eb" : "#f1f5f9",
                },
              }}
            >
              A
            </IconButton>

            {/* Botón D */}
            <IconButton
              size="small"
              onClick={() => setSelectionBarMode("side")}
              sx={{
                width: 30,
                height: 30,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: selectionBarMode === "side" ? "#3b82f6" : "white",
                color: selectionBarMode === "side" ? "white" : "#475569",
                border: "1px solid #cbd5e1",
                "&:hover": {
                  bgcolor: selectionBarMode === "side" ? "#2563eb" : "#f1f5f9",
                },
              }}
            >
              D
            </IconButton>
          </Box>

          {/* 🔵 MODO SUPERIOR */}
          {selectionBarMode === "top" && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 360,
                right: 0,
                height: 56,
                bgcolor: "#ffffffcc",
                backdropFilter: "blur(8px)",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                zIndex: 30,
              }}
            >
              <Typography
                sx={{ fontWeight: 600, fontSize: 14, color: "#334155" }}
              >
                {selectedEmails.length} seleccionados
              </Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  color="error"
                  onClick={() => setShowDeleteSelectedDialog(true)}
                  sx={{
                    bgcolor: "#fee2e2",
                    "&:hover": { bgcolor: "#fecaca" },
                    width: 30,
                    height: 30,
                  }}
                >
                  <DeleteIcon />
                </IconButton>

                <IconButton
                  onClick={markSelectedAsRead}
                  sx={{
                    bgcolor: "#e2f5ff",
                    "&:hover": { bgcolor: "#bae6fd" },
                    width: 30,
                    height: 30,
                  }}
                >
                  <DoneIcon sx={{ color: "#0284c7" }} />
                </IconButton>

                <IconButton
                  onClick={markSelectedAsUnread}
                  sx={{
                    bgcolor: "#f1f5f9",
                    "&:hover": { bgcolor: "#e2e8f0" },
                    width: 30,
                    height: 30,
                  }}
                >
                  <MailOutlineIcon sx={{ color: "#475569" }} />
                </IconButton>

                <IconButton
                  onClick={clearSelection}
                  sx={{
                    bgcolor: "#f1f5f9",
                    "&:hover": { bgcolor: "#e2e8f0" },
                    width: 30,
                    height: 30,
                  }}
                >
                  <CloseIcon sx={{ color: "#475569" }} />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* 🟣 MODO LATERAL DERECHO */}
          {selectionBarMode === "side" && (
            <Box
              sx={{
                position: "fixed",
                top: "50%",
                right: 20,
                transform: "translateY(-50%)",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                bgcolor: "#ffffffcc",
                backdropFilter: "blur(10px)",
                p: 1,
                borderRadius: "16px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                zIndex: 40,
              }}
            >
              <Tooltip title="Eliminar">
                <IconButton
                  color="error"
                  onClick={() => setShowDeleteSelectedDialog(true)}
                  sx={{
                    bgcolor: "#fee2e2",
                    "&:hover": { bgcolor: "#fecaca" },
                    width: 30,
                    height: 30,
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Marcar como leído">
                <IconButton
                  onClick={markSelectedAsRead}
                  sx={{
                    bgcolor: "#e2f5ff",
                    "&:hover": { bgcolor: "#bae6fd" },
                    width: 30,
                    height: 30,
                  }}
                >
                  <DoneIcon sx={{ color: "#0284c7" }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Marcar como no leído">
                <IconButton
                  onClick={markSelectedAsUnread}
                  sx={{
                    bgcolor: "#f1f5f9",
                    "&:hover": { bgcolor: "#e2e8f0" },
                    width: 30,
                    height: 30,
                  }}
                >
                  <MailOutlineIcon sx={{ color: "#475569" }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Cancelar">
                <IconButton
                  onClick={clearSelection}
                  sx={{
                    bgcolor: "#f1f5f9",
                    "&:hover": { bgcolor: "#e2e8f0" },
                    width: 30,
                    height: 30,
                  }}
                >
                  <CloseIcon sx={{ color: "#475569" }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </>
      )}

      {/* PANEL DERECHO */}
      <EmailPanelDerecho
        threads={conversationThreads}
        selectedPerson={selectedPerson}
        modoVista={modoVista}
        bodyCache={bodyCache}
        attachmentsByEmail={attachmentsByEmail}
        loadAttachments={loadAttachments}
        loadBodyCached={loadBodyCached}
        toggleRead={toggleRead}
        openReply={(email) => {
          setSelectedEmail(email);
          setShowReplyPanel(true);
        }}
        openForward={openForward}
        askDelete={(id) => {
          setDeleteId(id);
          setShowDeleteDialog(true);
        }}
        getAccountColor={getAccountColor} // ← 🔥 SOLUCIÓN
        selectionMode={selectionMode}
        selectedEmails={selectedEmails}
        toggleSelectEmail={toggleSelectEmail}
        clearSelection={clearSelection}
        selectionBarMode={selectionBarMode}
      />

      {/* SEND NEW EMAIL */}
      <EmailEnviarPro
        open={showSendPanel}
        onClose={() => setShowSendPanel(false)}
        accounts={accounts}
        onSent={loadEmails}
      />

      {/* RESPONDER */}
      <EmailContestar
        open={showReplyPanel}
        onClose={() => setShowReplyPanel(false)}
        email={selectedEmail}
        accounts={accounts}
        originalHtml={bodyCache[selectedEmail?.gmailId] || ""}
        onSend={async (form) => {
          await axios.post(`${API}/emails/send`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          loadEmails();
          setShowReplyPanel(false);
        }}
      />

      {/* REENVIAR */}
      <EmailReenviar
        open={showForwardPanel}
        onClose={() => setShowForwardPanel(false)}
        fromEmail={accounts[0]?.email}
        email={forwardEmail}
        forwardedHtml={forwardHtml}
        forwardOriginalAttachments={forwardOriginalAttachments}
        onSend={async (form) => {
          await axios.post(`${API}/emails/send`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          loadEmails();
          setShowForwardPanel(false);
        }}
      />

      {/* DELETE DIALOG */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">¿Eliminar email?</Typography>
          <Typography sx={{ color: "#777", mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
          >
            <Button onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
            <Button color="error" variant="contained" onClick={borrar}>
              Eliminar
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ACCOUNTS DRAWER */}
      <Dialog open={openAccounts} onClose={() => setOpenAccounts(false)}>
        <Box sx={{ width: 520, p: 2 }}>
          <AccountManager />
        </Box>
      </Dialog>

      {/* DELETE SELECTED DIALOG */}
      <Dialog
        open={showDeleteSelectedDialog}
        onClose={() => setShowDeleteSelectedDialog(false)}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">¿Eliminar emails seleccionados?</Typography>

          <Typography sx={{ color: "#777", mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
            }}
          >
            <Button onClick={() => setShowDeleteSelectedDialog(false)}>
              Cancelar
            </Button>

            <Button
              color="error"
              variant="contained"
              onClick={confirmDeleteSelected}
            >
              Eliminar
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
